import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import SharedDocumentPreview from 'components/ShareDocument/SharedDocumentPreview';
import UploadHubModalAdd, { UploadHubModalMode } from 'components/UploadHub/UploadHubModalAdd';
import UploadHubDeleteModal from 'components/UploadHub/UploadHubDeleteModal';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { AddToEmailCampaingPopup } from './AddToEmailCampaignButton';
import { AddToLobbyingMaterialPopup } from './AddToLobbyingMaterialButton';
import UploadHubPreviewPageMoreActions from './UploadHubPreviewPageMoreActions';
import type { UploadHubOverflowAction } from './UploadHubPreviewPageMoreActions';

const ACTION_ORDER = ['fileDetails', 'landingPageDesign', 'copyLink'] as const;
type ActionKey = (typeof ACTION_ORDER)[number];

const UploadHubPreviewPage = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { getUploadHubList } = useUploadFiles();
  const { addMetaTags } = useAddMetaTags();

  const [file, setFile] = useState<UploadHubListItem | null>(null);
  const [isLoading, setIsLoading] = useState(!file);
  const [editModalMode, setEditModalMode] = useState<Extract<UploadHubModalMode, 'editFile' | 'editDesign'> | null>(
    null
  );
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isOpenAddEmail, setIsOpenAddEmail] = useState<boolean>(false);
  const [isOpenAddLobbyingMaterial, setIsOpenAddLobbyingMaterial] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const linkCopiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCopiedOnceRef = useRef(false);
  const [overflowedActions, setOverflowedActions] = useState<ActionKey[]>([]);
  const actionsOuterRef = useRef<HTMLDivElement>(null);
  const actionsInnerRef = useRef<HTMLDivElement>(null);
  const fileDetailsBtnRef = useRef<HTMLButtonElement>(null);
  const landingPageDesignBtnRef = useRef<HTMLButtonElement>(null);
  const copyLinkBtnRef = useRef<HTMLButtonElement>(null);
  const actionBtnRefs = {
    fileDetails: fileDetailsBtnRef,
    landingPageDesign: landingPageDesignBtnRef,
    copyLink: copyLinkBtnRef,
  };
  /*const [passcodeEnabled, setPasscodeEnabled] = useState(false);
  const [isPasscodeModalOpen, setIsPasscodeModalOpen] = useState(false);*/

  const fetchFile = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFile.current = async (signal) => {
    try {
      const results = await getUploadHubList({ signal });
      const file = results?.find((item: UploadHubListItem) => item.publicId === publicId) ?? null;
      setFile(file);
      const title = `Your files - ${file ? `${file?.fileName} - ` : ''}PolicyMogul`;
      addMetaTags({ title, hash: location.hash });
      setIsLoading(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchFile.current?.(controller.signal);
    return () => {
      controller.abort('Upload hub preview cancelled.');
      if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    };
  }, []);

  const copyLandingPageLink = () => {
    if (!file) return;
    navigator.clipboard?.writeText(buildDocumentSharePageV2Url(file.publicId));
    hasCopiedOnceRef.current = true;
    setLinkCopied(true);
    if (linkCopiedTimeout.current) clearTimeout(linkCopiedTimeout.current);
    linkCopiedTimeout.current = setTimeout(() => setLinkCopied(false), 2000);
  };

  useLayoutEffect(() => {
    const outer = actionsOuterRef.current;
    const inner = actionsInnerRef.current;
    if (!file || !outer || !inner) return;

    const measure = () => {
      ACTION_ORDER.forEach((key) => actionBtnRefs[key].current?.classList.remove('d-none'));
      const hidden: ActionKey[] = [];
      for (const key of ACTION_ORDER) {
        if (inner.scrollWidth <= outer.clientWidth + 1) break;
        actionBtnRefs[key].current?.classList.add('d-none');
        hidden.push(key);
      }
      setOverflowedActions(hidden);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [file]);

  const overflowActions: UploadHubOverflowAction[] = overflowedActions.map((key) => {
    if (key === 'fileDetails') {
      return {
        key,
        icon: 'icon-bullet-list',
        label: 'File details',
        onClick: () => {
          setEditModalMode('editFile');
          setOpenEditModal(true);
        },
      };
    }
    if (key === 'landingPageDesign') {
      return {
        key,
        iconUrl: `${import.meta.env.VITE_APP_CDNURL}/images/20240121-reports-design-icon.png`,
        label: 'Landing page design',
        onClick: () => {
          setEditModalMode('editDesign');
          setOpenEditModal(true);
        },
      };
    }
    return {
      key,
      icon: 'icon-copy',
      label: 'Copy link to landing page',
      onClick: () => {
        if (!file) return;
        navigator.clipboard?.writeText(buildDocumentSharePageV2Url(file.publicId));
        createNotification('success', 'Link copied');
      },
    };
  });

  const [heightContainer, containerRef] = useHeightContainer();

  return (
    <div className='upload-hub-preview-page'>
      <div className='bg-white monitor-items-bar z-extra-max overflow-auto'>
        <div className='mx-auto main-content-wrapper flex-centered justify-content-between resize-elements-container'>
          <div className='side-left-element pl-md-5 pl-3 d-lg-flex flex-centered'>
            <Link
              className='general-button action-button rounded-pill px-4 paragraph-p2 d-inline-block nowrap-item'
              to={'/files'}
            >
              <span className='icon-long-arrow-left mr-2 paragraph-p4' /> Back{' '}
              <span className='d-none d-lg-inline-block'>to files</span>
            </Link>
          </div>
          <div className='main-content flex-centered position-relative filters-general-container justify-content-between pr-xl-5 px-md-4 px-3'>
            <h4
              className='mb-0 flex-centered justify-content-between group-view-title flex-grow-1 upload-hub-file-title'
              style={{ minWidth: '180px' }}
              onClick={() => {
                setEditModalMode('editFile');
                setOpenEditModal(true);
              }}
            >
              <span className='hidden-lines main-title hidden-one-line line-height-1-2'>{file?.fileName}</span>
              <span className='d-none d-lg-flex bg-light-blue text-white rounded-circle ml-3 mr-2 flex-centered justify-content-center group-view-edit-button upload-hub-edit-button'>
                <span className='icon-edit-pencil paragraph-p3' />
              </span>
            </h4>
            {isLoading ? (
              <div className='flex-centered'>
                <SkeletonTheme baseColor='#f2f2f2'>
                  <Skeleton height={36} width={100} borderRadius={18} className='mr-3' />
                  <Skeleton height={36} width={90} borderRadius={18} className='mr-3' />
                  <Skeleton height={36} width={36} circle className='mr-2' />
                  <Skeleton height={36} width={36} circle className='ml-1' />
                </SkeletonTheme>
              </div>
            ) : (
              file && (
                <>
                  <div
                    className='flex-centered justify-content-end flex-grow-1 overflow-hidden'
                    style={{ minWidth: 0 }}
                    ref={actionsOuterRef}
                  >
                    <div className='flex-centered' style={{ flexShrink: 0 }} ref={actionsInnerRef}>
                      <button
                        ref={landingPageDesignBtnRef}
                        className='general-button rounded-pill border pr-3 specific-left-padding-10 specific-y-padding-5 rounded-pill mr-3 hovered-button report-design-button bg-main-white upload-design-button nowrap-item'
                        onClick={() => {
                          setEditModalMode('editDesign');
                          setOpenEditModal(true);
                        }}
                      >
                        Landing page design
                      </button>
                      {/* <button
                      className='general-button border specific-y-padding-5 specific-x-padding-22 mr-3 rounded-button d-flex nowrap-item bg-main-white'
                      onClick={() => {
                        const next = !passcodeEnabled;
                        setPasscodeEnabled(next);
                        if (next) setIsPasscodeModalOpen(true);
                      }}
                    >
                      <span>Passcode </span>
                      <span
                        className='icon-info specific-top-margin-6 specific-left-margin-8 paragraph-p3'
                        data-tooltip-id='general-tooltip'
                        data-tooltip-content='Require a passcode to open this file'
                        data-tooltip-place='bottom'
                      />
                      <span
                        className={`specific-left-margin-18 toggle-button-${passcodeEnabled ? 'active' : 'inactive'}`}
                      >
                        <span className='toggle-button' />
                      </span>
                    </button> */}
                      <button
                        ref={copyLinkBtnRef}
                        className=' general-button rounded-pill border pr-3 specific-left-padding-13 specific-y-padding-5 rounded-pill specific-right-margin-1 hovered-button bg-main-white text-left'
                        onClick={copyLandingPageLink}
                        style={{ minWidth: '227px' }}
                      >
                        <span
                          key={linkCopied ? 'copied' : 'idle'}
                          className={`upload-hub-copy-link-label${hasCopiedOnceRef.current ? '' : ' no-animate'}`}
                        >
                          <span
                            className={`${linkCopied ? 'icon-small-check paragraph-p1 text-green-create' : 'icon-copy paragraph-p3'} mr-2 d-inline-block-centered `}
                          />
                          {linkCopied ? 'Link to page copied' : 'Copy link to landing page'}
                        </span>
                      </button>

                      <button
                        ref={fileDetailsBtnRef}
                        className='general-button rounded-pill border pr-3 specific-left-padding-10 specific-y-padding-5 ml-3 hovered-button bg-main-white nowrap-item'
                        onClick={() => {
                          setEditModalMode('editFile');
                          setOpenEditModal(true);
                        }}
                      >
                        <span className='icon-three-dots paragraph-p3 mr-2 main-light-text d-inline-block-centered' />
                        File details
                      </button>
                      <UploadHubPreviewPageMoreActions
                        file={file}
                        setIsOpenAddEmail={setIsOpenAddEmail}
                        setIsDeleteOpen={setIsDeleteOpen}
                        setIsOpenAddLobbyingMaterial={setIsOpenAddLobbyingMaterial}
                        overflowActions={overflowActions}
                      />
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>

      <div className='upload-hub-preview-content' ref={containerRef}>
        <CustomScrollbar
          className='simple-scrollbar'
          style={{ height: `${heightContainer}px` }}
          maximalThumbYSize={100}
        >
          {isLoading ? (
            <SharedDocumentPreviewSkeleton />
          ) : !file ? (
            <div className='row mx-auto main-content-wrapper align-items-start flex-grow-1 pt-5'>
              <div className='w-100 px-xl-5 px-md-4 px-3 h-100 mx-auto'>
                <p className='text-center mt-5 pt-5 title-h4 main-light-text px-5 mt-5 pt-lg-5'>File not found</p>
              </div>
            </div>
          ) : (
            <div className='mx-auto main-content-wrapper px-3 px-lg-5 upload-hub-preview-document'>
              <SharedDocumentPreview
                document={{
                  documentUrl: file.url,
                  fileName: file.fileName,
                  contentType: file.contentType,
                  backgroundColor: file.backgroundColor,
                  barBackgroundColor: file.barBackgroundColor,
                  organisationLogo: file.organisationLogoUrl,
                }}
              />
            </div>
          )}
        </CustomScrollbar>
      </div>

      {file && (
        <>
          <UploadHubModalAdd
            mode={editModalMode ?? 'editFile'}
            editingFile={file}
            openState={{
              isOpen: openEditModal,
              setIsOpen: setOpenEditModal,
            }}
            onUpdated={setFile}
          />
          <UploadHubDeleteModal
            openState={{ isOpen: isDeleteOpen, setIsOpen: setIsDeleteOpen }}
            itemIdState={{ itemId: file.id, setItemId: () => {} }}
            onDeleted={() => navigate('/files')}
          />

          <AddToEmailCampaingPopup openState={{ isOpen: isOpenAddEmail, setIsOpen: setIsOpenAddEmail }} file={file} />
          <AddToLobbyingMaterialPopup
            openState={{ isOpen: isOpenAddLobbyingMaterial, setIsOpen: setIsOpenAddLobbyingMaterial }}
            file={file}
          />
        </>
      )}
    </div>
  );
};

const SharedDocumentPreviewSkeleton = () => (
  <div className='mx-auto main-content-wrapper px-3 px-lg-5'>
    <SkeletonTheme baseColor='#f2f2f2'>
      <div className='share-preview-controls pt-5 bg-grey'>
        <div className='px-3 px-lg-5 py-2 rounded-top'>
          <div className='main-content-wrapper d-flex align-items-center mx-auto'>
            <div className='d-flex align-items-center' style={{ minWidth: 0 }}>
              <Skeleton width={28} height={28} borderRadius={4} className='mr-2' />
              <Skeleton width={220} height={16} />
            </div>
            <div className='d-flex align-items-center ml-auto pl-3 flex-shrink-0' style={{ gap: '10px' }}>
              <Skeleton width={96} height={30} borderRadius={16} />
              <Skeleton width={132} height={30} borderRadius={16} />
              <Skeleton width={104} height={30} borderRadius={16} />
            </div>
          </div>
        </div>
      </div>
      <div className='share-document-page main-content-wrapper px-3 px-lg-5 pt-5 mx-auto' style={{ maxWidth: '920px' }}>
        <div className='share-document-preview mx-auto'>
          <div className='rounded border-general border-grey-50'>
            <div className='attachment-preview-height-container'>
              <Skeleton height='100%' width='100%' borderRadius={0} style={{ display: 'block', lineHeight: 1 }} />
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  </div>
);

export default UploadHubPreviewPage;
